<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>

import GameService from '../../services/game/game.service';
import { getDataUrl,getNeutralColors } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import * as axios from 'axios';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';
import Dictionary from '../../dictionary';

export default {
  name: 'XAChart',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    margin: {
      type: Object,
      // {top: 0, right: 0, bottom: 0, left: 0}
      required: false,
    },
    type : {
      type: String,
      required: true,
    },
    passes: {
      type: Array,
      required: true,
    },
    playerName: {
      type: String,
      required: false
    },
    seasons: {
      type: Array,
      required: false
    },
    game: {
      type: Object,
      required: false
    },
    circlesAndBoxes: {
      type: Array,
      required: false
    },
    language: {
      type: String,
      required: false
    },
    homeFillColor:{
      type: String,
      default: "#ffffff"
    },
    awayFillColor:{
      type: String,
      default: "#ffffff"
    },
    homeBorderColor:{
      type: String,
      default: "#ffffff"
    },
    awayBorderColor:{
      type: String,
      default: "#ffffff"
    },
    mediaMode: {
      type: Boolean,
      default: false
    },
    postGameAnalysis:{
      type: Boolean,
      default: false
    },
    homeTeamName:{
      type: String,
      default: ""
    },
    awayTeamName:{
      type: String,
      default: ""
    },
    team:{
      type: Boolean,
      default: false
    },
    //gameId for print one game Player xA Map
    gameId:{
      type:Number,
      default: 0
    },
    //gameIds for print games of month Team xA Map
    gameIds: {
      type: Array,
      required: false,
      default: () => []
    },
    gameName:{
      type: String,
      default: ""
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    },
    minsPlayed: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      height: 0,
      width: 0,
      images: {}
    }
  },
  computed: {
    SVG_WIDTH(){
      return this.size ? this.size : 380;
    },
    PITCH_WIDTH(){
      if(this.margin){
        return this.SVG_WIDTH - this.margin.left - this.margin.right
      }else{
        return this.SVG_WIDTH;
      }
    },
    PITCH_HEIGHT() {
      return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
    },
    rootElementId(){
      return `pitchXA${this.id}`
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    }
  },
  mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
    this.printD3();
  },
  methods: {
    async printD3(force = false){
      if(force){
        this.images = {};
      }

      const SVG_WIDTH = this.SVG_WIDTH;
      const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;

      d3.selectAll('.tooltip').remove();

      d3.select(`#${this.rootElementId}`).select("svg").remove();

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip");

      var svg = d3.select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", SVG_WIDTH)
        .attr("height", PITCH_HEIGHT)
        .style("background-color", "#ffffff")
        .attr("id",`${this.rootElementId}SVG`)
        .attr("class", "pitch");

      // DRAW PITCH
      this.drawImageBase64(svg, margin.left, margin.top, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/${this.type === "game" ? "": "half_"}pitch_${this.currentUserFieldColor}.png`),'pitch',true);

      let neutralColorsHome = getNeutralColors('home');
      let firstHomeTeamColor = this.homeFillColor;
      let secondHomeTeamColor = this.homeBorderColor;
      let neutralColorsAway = getNeutralColors('away');
      let firstAwayTeamColor = this.awayFillColor;
      let secondAwayTeamColor = this.awayBorderColor;
      //If is game xA Map but it's post game, don't get team colors, keep neutral colors
      if (this.type === "game" && !this.postGameAnalysis && !this.mediaMode) {
        firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
        secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
        firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
        secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
      }
      if(this.type === "game" && firstHomeTeamColor === firstAwayTeamColor){
        firstAwayTeamColor = neutralColorsAway[0];
      }
      if(this.type === "game" && secondHomeTeamColor === secondAwayTeamColor){
        secondAwayTeamColor = neutralColorsAway[1];
      }

      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      if (this.type === "player") {
        // DRAW LOGO
        this.drawImageBase64(svg, PITCH_WIDTH * 0.735 + margin.left , this.type === "game" ? PITCH_HEIGHT * 0.77 + margin.top : PITCH_HEIGHT * 0.785 + margin.top, PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18, logo,"logoDriblab");
        this.drawImageBase64(svg, PITCH_WIDTH * 0.09 + margin.left , this.type === "game" ? PITCH_HEIGHT * 0.7 + margin.top : PITCH_HEIGHT * 0.77 + margin.top, PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18,
          require(`@/assets/img/legend_xa_${this.language === 'en' ? 'english' : 'spanish'}${this.currentUserFieldColor === 'white' ? "_dark": ""}_vertical.png`),"pitchLegend");

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles")) {
          this.renderXAPassSegments(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, this.type === "player");
          this.renderXACircles(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        }
        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes")) {
          this.renderBoxes(svg,
            SVG_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.passes.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal),
            this.language
          )
        }
        this.printPlayerXALegend(svg, PITCH_WIDTH, margin, this.passes, this.seasons, this.playerName, this.language);

      } else if (this.type === "game") {
        // DRAW LOGO
        this.drawImageBase64(svg, PITCH_WIDTH * 0.735 + margin.left , this.type === "game" ? PITCH_HEIGHT * 0.77 + margin.top : PITCH_HEIGHT * 0.785 + margin.top, PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18, logo,"logo");
        this.drawImageBase64(svg, PITCH_WIDTH * 0.075 + margin.left , this.type === "game" ? PITCH_HEIGHT * 0.86 + margin.top : PITCH_HEIGHT * 0.78 + margin.top, PITCH_WIDTH * 0.38, PITCH_HEIGHT * 0.18,
        require(`@/assets/img/legend_xa_${this.language === 'en' ? 'english' : 'spanish'}${this.currentUserFieldColor === 'white' ? "_dark": ""}.png`),"pitchLegend");

        this.renderXAPassSegments(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, this.type === "player");
        this.renderXACircles(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        this.printXALegend(svg, PITCH_WIDTH, margin, this.passes)
        this.printGameLegend(svg, PITCH_WIDTH, PITCH_HEIGHT, margin, firstHomeTeamColor, firstAwayTeamColor, this.game, this.passes)
      }
      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

      //Emit when is loaded
      this.$emit("loaded", this.id);
    },
    measureText(string, fontSize = 10) {
      const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
        0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
        0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
        0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
        0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
        0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
        0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
        0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
      const avg = 0.5293256578947368;

      return string
        .split('')
        .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
        .reduce((cur, acc) => acc + cur) * fontSize;
    },
    colorScale(minDomain, maxDomain, value, steps) {
        //Range of colors in HSV format gradient
        var colorsArray = ["#fb0e0e","#fb130e","#fb180f","#fa1d0f","#fa220f","#fa270f","#fa2c10","#fa3110","#f93610","#f93b11","#f94011","#f94511","#f84911","#f84e12","#f85312",
        "#f85812","#f85d13","#f76113","#f76613","#f76b13","#f76f14","#f77414","#f67914","#f67d14","#f68215","#f68715","#f58b15","#f59016","#f59416","#f59916","#f59d16","#f4a217",
        "#f4a617","#f4aa17","#f4af17","#f4b318","#f3b818","#f3bc18","#f3c019","#f3c519","#f3c919","#f2cd19","#f2d11a","#f2d61a","#f2da1a","#f1de1a","#f1e21b","#f1e61b","#f1ea1b",
        "#f1ee1b","#eef01c","#eaf01c","#e5f01c","#e1f01d","#dcf01d","#d8ef1d","#d4ef1d","#cfef1e","#cbef1e","#c6ee1e","#c2ee1e","#beee1f","#baee1f","#b5ee1f","#b1ed1f","#aded20",
        "#a9ed20","#a4ed20","#a0ed20","#9cec21","#98ec21","#94ec21","#90ec21","#8cec22","#88eb22","#84eb22","#80eb22","#7ceb23","#78ea23","#74ea23","#70ea23","#6cea24","#68ea24",
        "#64e924","#60e924","#5ce925","#58e925","#54e925","#51e825","#4de826","#49e826","#45e826","#42e726","#3ee727","#3ae727","#37e727","#33e727","#2fe628","#2ce628","#28e628"];

        //Get scale functions.
        // - Range: Number of gradient steps
        // - Domain: Range of values
        let colorScale = d3.scale.linear().domain([minDomain, maxDomain]).range([0, steps - 1]);
        //Get index of colorsArray
        var index = colorScale(value);
        //Get whole part of the index
        var color = colorsArray[Math.trunc(index)];

        return color;
    },
    renderXAPassSegments(svg, chartSizeRate, chartWidth, chartHeight, segments, teamColor, isPlayerXA) {

      let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
      let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

      let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);

      // ====================================================================
      //        Render Segments
      // ====================================================================
      for (let segment of segments) {
        let sx0;
        let sy0;
        let sx1;
        let sy1;
        if (isPlayerXA) {
          sx0 = x(segment.y);
          sy0 = y(100 - (100 - segment.x) * 2);
          sx1 = x(segment.y_end);
          sy1 = y(2 * segment.x_end - 100);
        } else if (segment.homeTeam) {
          sx0 = x(segment.x);
          sy0 = y(100 - segment.y);
          sx1 = x(segment.x_end);
          sy1 = y(100 - segment.y_end);
        } else {
          sx0 = x(100 - segment.x);
          sy0 = y(segment.y);
          sx1 = x(100 - segment.x_end);
          sy1 = y(segment.y_end);
        }
        this.drawXALine(svg, sx0, sy0, sx1, sy1, radius(segment.width) * 1.5, segment.xG, teamColor, 0.8);
      }
    },
    async drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
      if(!this.images[idName]){
        let base_image = new Image();
        base_image.crossOrigin = "anonymous";
        base_image.src = img;
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          x,
          y,
          width,
          height,
          dataUrl,
          idName,
          bottom
          );
        }
      }else{
        if(bottom){
          svg.node().insertBefore(this.images[idName],svg.node().firstChild);
        }else{
          svg.node().appendChild(this.images[idName]);
        }
      }
    },
    drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
      let imgNode = bottom ? svg.insert("svg:image",':first-child') : svg.append("svg:image");
        imgNode.attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
    },
    drawRectangle(svg, x, y, width, height, color, opacity, className) {
      return svg.append("rect")
        .attr("class", className)
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .style("fill", color)
        .style("opacity", opacity);
    },
    writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
      svg.append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", anchor)
        .attr("dy", "0.8em")
        .attr("font-weight", 500)
        .attr("class", className)
        .style("font-size", fontSize)
        .style("font-family", this.fontFamily)
        .style("fill", fill)
        .text(text);
    },
    getTypeOfShot(name) {
      if (name === "SAVED SHOT") return "Saved Shot";
      if (name === "GOAL") return "Goal";
      if (name === "MISSED SHOT") return "Missed Shot";
      if (name === "SHOT ON POST") return "Shot on Post";
      if (name === "MISSED CHANCE") return "Chance Missed";
      if (name === "Regular play") return "Regular Play";
      if (name && name.startsWith("Individual")) return "Individual Play";
      if (name === "Fast break") return "Fast Break";
      if (name === "From corner") return "From Corner";
      if (name === "Free kick") return "Free Kick";
      if (name === "Set piece") return "Set Piece";
      if (name && name.startsWith("Throw")) return "Throw In";
      if (name === "Left footed") return "Left Foot";
      if (name === "Right footed") return "Right Foot";
      if (name === "Head") return "Head";
      if (name === "Other body part") return "Other Body Part";
      if (name === "Keeper Throw") return "Keeper Throw";
      console.log("Unkown " + name);
      return "??";
    },
    getTypeOfPass(name) {
      if (name === "pass") return "Key Pass";
      if (name === "assist" || name === "goal") return "Assist";
      return "Key Pass";
    },
    drawXACircle(svg, chartSizeRate,  x, y, circle, i, radius, xa, tooltip, isAssist) {
      var self = this;
      svg.append("circle")
        .attr("r", radius * chartSizeRate)
        .attr("cx", x)
        .attr("cy", y)
        .attr('id', "pass" + i + "-" + circle.teamName)
        .style("fill-opacity", 0.6)
        .style("opacity", 0.8)
        .style("stroke",  isAssist ? "#000000" : "#" + this.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1))
        .style("stroke-width", 2 * chartSizeRate)
        .style("fill", "#" + this.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1))
        .on('mouseenter',function(d) {
          d3.select(this)
            .transition()
            .duration(300)
            .style("cursor", "pointer")
            .attr('r', radius * 1.75);

          var textLine1 = circle.game;
          var textLine2 = self.getTypeOfPass(circle.type) + " to "+ circle.player + " at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec);
          var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14));
          var widthTooltip = maxTextWidth + 60;
          var height = self.type != "game" ? 70 : 110;
          height = self.team ? 90 : height;

          let tooltipText = self.type != "game" ? "<b>" + circle.game + "</b></br>" +
                  (self.team ? `${circle.playerFrom}</br>` : '') +
                  circle.xG.toFixed(2) + " xA</br>" +
                  self.getTypeOfPass(circle.type) + " to "+ circle.player + " at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec)
          : "<b>" + circle.game + "</b></br>" +
                  circle.xG.toFixed(2) + " xA</br>" +
                  self.getTypeOfPass(circle.type) + "</br>" +
                  circle.playerFrom + " to "+ circle.player + "</br>at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec);

          var shotId = document.getElementById("pass" + i + "-" + circle.teamName);
          var circleRadio = Math.ceil(shotId.getAttribute('r'));
          let tooltipX = self.$vuetify.breakpoint.mobile ? (window.innerWidth/2) - (widthTooltip/2): shotId.getBoundingClientRect().left + circleRadio - widthTooltip / 2
          let tooltipY = self.$vuetify.breakpoint.mobile ? d3.select(`#${self.rootElementId}`).node().getBoundingClientRect().top + window.scrollY + (height/2) : (shotId.getBoundingClientRect().top + window.scrollY +  circleRadio - height - 30)
          d3.select("body > .tooltip")
            .style("width", widthTooltip + "px")
            .style("height", height + "px")
            .style("left", tooltipX + "px")
            .style("top", tooltipY + "px")
            .style("display", "inline-block")
            .style("font-size", "14px")
            .html(tooltipText);
        })
        .on('mouseout',function() {
          d3.select(this)
            .transition()
            .duration(200)
            .attr('r', radius);
          d3.select("body > .tooltip").style("display", "none");
        });
    },
    drawXALine(svg, x0, y0, x1, y1, width, xa, strokeColor = "black", opacity = 1) {
      var self = this;
      svg.append("line")
        .attr("x1", x0)
        .attr("y1", y0)
        .attr("x2", x1)
        .attr("y2", y1)
        .attr("stroke-width", 0.002 * this.SVG_WIDTH)
        .style("opacity", 1)
        .attr("stroke", "#" + self.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1));
    },
    renderXACircles(svg, chartSizeRate, chartWidth, chartHeight, circles, teamColor, strokeColor, tooltip, isPlayer) {

      let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
      let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

      let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
      for (let i in circles) {
        let sx;
        let sy;
        if (isPlayer) {
          sx = x(circles[i].y_end);
          sy = y(2 * circles[i].x_end - 100);
        } else if (circles[i].xG) {
          sx = circles[i].homeTeam ? x(circles[i].x_end) : x(100 - circles[i].x_end);
          sy = circles[i].homeTeam ? y(100 - circles[i].y_end) : y(circles[i].y_end);
        } else {
          sx = x(100 - circles[i].x_end);
          sy = y(circles[i].y_end);
        }
        this.drawXACircle(svg, chartSizeRate, sx, sy, circles[i], i, radius(circles[i].xG), circles[i].xG, tooltip, circles[i].type === "GOAL" ? true : false);
      }
    },
    printPlayerXALegend(svg, pitchWidth, margin, shots, seasons, playerName, language = 'en') {

      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const numberOfPasses = shots.length;
      const xG = shots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
      const xGSetPiece = shots.filter(p => p.strategy === 'Set piece' || p.strategy === 'From corner' || p.strategy === 'Free Kick' || (p.strategy && p.strategy.startsWith("Throw"))).map(shot => shot.xG).reduce((a, b) => a + b, 0);
      const xGOpenPlay = shots.filter(p => p.strategy === 'Regular play' || p.strategy === 'Fast break' || (p.strategy && p.strategy.startsWith("Individual"))).map(shot => shot.xG).reduce((a, b) => a + b, 0);
      const games = this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0);
      const minutes = this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0);
      const numberOfSeasons = seasons.length - 1;
      const rectangleSize = 0.16 * pitchWidth + numberOfSeasons * 0.023 * pitchWidth;
      let xADecimals = 1;

      if(this.gameId == 0){
        let headerText = `${ Dictionary[this.language]["table"]["history"]["games"] }: ${ games }`;
        if(!this.team){
          //Just show minutes for Player FinishingMap
          headerText += ` · ${ Dictionary[this.language]["table"]["roster"]["minutes"] }: ${ minutes }`;
        }
        this.writeText(svg, pitchWidth * 0.09, 0.045 * pitchWidth, "start", (0.018 * pitchWidth) + "px",
        headerText, textColor, "playerGamesAndMinutes");

      }else{
        xADecimals = 2;
      }

      this.writeText(svg, pitchWidth * 0.92, 0.008 * pitchWidth, "end", (0.016 * pitchWidth) + "px",
      `${ numberOfPasses } ${ Dictionary[this.language]["keyPassMap"]["chart"]["keyPasses"] } · ${ numberOfPasses > 0 ? (xG / numberOfPasses).toFixed(xADecimals+1) : 0 } ${ Dictionary[this.language]["keyPassMap"]["chart"]["xAPerPass"] }`,
      textColor, "playerShotsAndXG");

      this.writeText(svg, pitchWidth * 0.92, 0.028 * pitchWidth, "end", (0.016 * pitchWidth) + "px",
      `${ xGOpenPlay.toFixed(xADecimals) } ${ Dictionary[this.language]["keyPassMap"]["chart"]["xAOpenPlay"] }`, textColor, "playerShotsAndXG");

      this.writeText(svg, pitchWidth * 0.92, 0.048 * pitchWidth, "end", (0.016 * pitchWidth) + "px",
      `${ xGSetPiece.toFixed(xADecimals) } ${ Dictionary[this.language]["keyPassMap"]["chart"]["xASetPiece"] }`, textColor, "playerShotsAndXG");

      this.drawRectangle(svg,
        (pitchWidth / 2 - 0.45 * pitchWidth / 2) + margin.left, // x
        pitchWidth * 0.58 - numberOfSeasons * 0.023 * pitchWidth, // y
        0.45 * pitchWidth, // width
        rectangleSize, // height
        "black",
        0.8,
        "xgPlayerLegendRect");

      this.writeText(svg, pitchWidth * 0.505 + margin.left, 0.6 * pitchWidth + margin.top - numberOfSeasons * 0.025 * pitchWidth, "middle", (0.026 * pitchWidth) + "px", playerName, "white", "playerXGName");

      svg.selectAll(".leagueAlias")
        .transition()
        .duration(200)
        .style("font-size", "0px");

      if(this.gameId == 0 && this.gameName == ""){
        //Print season's names
        svg.selectAll(".leagueAlias")
          .data(seasons)
          .enter()
          .append("text")
          .attr("class", "leagueAlias")
          .attr("x", pitchWidth / 2 + margin.left)
          .attr("y", (d, i) => i * 0.025 * pitchWidth + 0.65 * pitchWidth + margin.top - numberOfSeasons * 0.025 * pitchWidth)
          .attr("text-anchor", "middle")
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .style("font-size", (0.021 * pitchWidth) + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(d => d.seasonName);
      }else{
        //Print game name
        this.writeText(svg,
          pitchWidth / 2 + margin.left,
          0.65 * pitchWidth + margin.top,
          "middle",
          (0.021 * pitchWidth) + "px",
          this.gameName,
          "white",
          "gameName"
        );
      }

      this.writeText(svg, pitchWidth * 0.505,
          0.695 * pitchWidth,
          "middle",
          (0.023 * pitchWidth) + "px",
          `${ shots.filter(p => p.type === "GOAL").length } ${ Dictionary[this.language]["team"]["overviewTab"]["stats"]["assists"] } · ${ xG.toFixed(xADecimals) } xA`,
          "white", "playerXGandGoals");

    },
    getPlayersWithHighestXG(shots) {
      // Group players by name
      const sumXGShots = shots.reduce(function(acc, val){
        var o = acc.filter(function(obj){
            return obj.playerFrom === val.playerFrom;
        }).pop() || {playerFrom: val.playerFrom, xG: 0};

        o.xG += val.xG;
        const index = acc.findIndex(p => p.playerFrom === o.playerFrom)
        if (index === -1)
          acc.push(o);
        else
          acc[index] = o;
        return acc;
      },[]);
      // Sort, take out players with xG < 0.1 and return first three
      return sumXGShots.sort((a, b) => b.xG - a.xG).filter(p => p.xG >= 0.1).slice(0, 3);
    },
    printGameLegend(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      let secondHomeTeamColor = this.homeBorderColor;
      let secondAwayTeamColor = this.awayBorderColor;

      let rectHome = this.drawRectangle(svg,
          0.045 * pitchSize + margin.left,
          margin.top - 3,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
                  rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)
      this.drawImageBase64(svg,
        0.07 * pitchSize + margin.left,
        0.03 * pitchHeight + margin.top,
        0.05 * pitchSize,
        0.05 * pitchHeight,
        'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png',"homeCrest");
      this.writeText(svg,
        0.115 * pitchSize + margin.left,
        0.04 * pitchHeight + margin.top,
        "start",
        (0.022 * pitchSize) + "px",
        `${ this.homeTeamName } • ${ gameData.homeTeamScore }`,textColor);

      let rectAway = this.drawRectangle(svg,
        0.935 * pitchSize + margin.left,
        margin.top - 3,
        16,
        (0.075 * pitchHeight) + 5,
        awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
      rectAway
      .style("border-radius",'3px')
      .style("border",`1px solid ${secondAwayTeamColor}`)
      .style("opacity",0.95)
      .style("stroke-width",1)
      .style("stroke",`${secondAwayTeamColor}`)
      .attr("rx",3)
      .attr("ry",3)
      this.drawImageBase64(svg,
        0.885 * pitchSize + margin.left,
        0.03 * pitchHeight + margin.top,
        0.05 * pitchSize,
        0.05 * pitchHeight,
        'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png',"awayCrest");
      this.writeText(svg,
        0.89 * pitchSize + margin.left,
        0.04 * pitchHeight + margin.top,
        "end",
        (0.022 * pitchSize) + "px",
        `${ gameData.awayTeamScore } • ${ this.awayTeamName }`,textColor);
    },
    printXALegend(svg, pitchWidth, margin, shots) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      let homeShots = shots.filter(s => s.homeTeam);
      let awayShots = shots.filter(s => !s.homeTeam);

      let homeOwnGoals = homeShots.length === 0 ? 0 : homeShots.filter(d => d.ownGoal).length;
      let awayOwnGoals = awayShots.length === 0 ? 0 : awayShots.filter(d => d.ownGoal).length;

      let homeGoals = homeShots.length === 0 ? 0 : homeShots.filter(d => d.type === "GOAL").length - homeOwnGoals + awayOwnGoals;
      let awayGoals = awayShots.length === 0 ? 0 : awayShots.filter(d => d.type === "GOAL").length - awayOwnGoals + homeOwnGoals;

      let homeXG = homeShots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
      let awayXG = awayShots.map(shot => shot.xG).reduce((a, b) => a + b, 0);

      const rectangleSize = 0.28 * pitchWidth;

      let xaLegend = this.currentUserLanguage === 'en' ? "Sum of xA" : "Suma de xA";

      this.drawRectangle(svg,
        (pitchWidth / 2 - rectangleSize / 2) + margin.left,
        margin.top,
        rectangleSize,
        rectangleSize * 0.35,
        "black",
        0.8,
        "xgLegendRect");
      this.writeText(svg, pitchWidth / 2 + margin.left, 0.012 * pitchWidth + margin.top, "middle", (0.017 * pitchWidth) + "px", xaLegend);
      this.writeText(svg, pitchWidth / 2 + margin.left - 0.095 * pitchWidth, 0.03 * pitchWidth + margin.top, "start", (0.02 * pitchWidth) + "px", `${ homeGoals > homeXG ? '+' : '' }${ (homeGoals - homeXG).toFixed(1) }`, homeGoals > homeXG ? "green" : "red");
      this.writeText(svg, pitchWidth / 2 + margin.left + 0.095 * pitchWidth, 0.03 * pitchWidth + margin.top, "end", (0.02 * pitchWidth) + "px", `${ awayGoals > awayXG ? '+' : '' }${ (awayGoals - awayXG).toFixed(1) }`, awayGoals > awayXG ? "green" : "red");
      this.writeText(svg, pitchWidth / 2 + margin.left, 0.0525 * pitchWidth + margin.top, "middle", (0.04 * pitchWidth) + "px", `${ homeXG.toFixed(1) } • ${awayXG.toFixed(1)}`);

      let homeXGPlayers = this.getPlayersWithHighestXG(homeShots);
      let awayXGPlayers = this.getPlayersWithHighestXG(awayShots);

      homeXGPlayers.forEach((player, i) => {
        this.writeText(svg, pitchWidth * 0.495 + margin.left, (0.108 + (0.024 * i)) * pitchWidth + margin.top, "end", (0.018 * pitchWidth) + "px", `${player.playerFrom} · ${player.xG.toFixed(2)}`,textColor);
      });

      awayXGPlayers.forEach((player, i) => {
        this.writeText(svg, pitchWidth * 0.51 + margin.left, (0.108 + (0.024 * i)) * pitchWidth + margin.top, "start", (0.018 * pitchWidth) + "px", `${player.xG.toFixed(2)} · ${player.playerFrom}`,textColor);
      });

      let homePenaltyGoals = homeShots.length === 0 ? 0 : homeShots.filter(d => d.penaltyGoal).length;
      let awayPenaltyGoals = awayShots.length === 0 ? 0 : awayShots.filter(d => d.penaltyGoal).length;
      let homeXAPerPassValue = isNaN((homeXG / (homeShots.length - homePenaltyGoals))) ? 0 : (homeXG / (homeShots.length - homePenaltyGoals));
      let awayXAPerPassValue = isNaN((awayXG / (awayShots.length - awayPenaltyGoals))) ? 0 : (awayXG / (awayShots.length - awayPenaltyGoals));

      this.writeText(svg, pitchWidth * 0.495 + margin.left, 0.59 * pitchWidth + margin.top, "end", (0.016 * pitchWidth) + "px", `${ homeShots.length - homePenaltyGoals } ` + Dictionary[this.language]["keyPassMap"]["chart"]["keyPasses"] + ` · ${ homeXAPerPassValue.toFixed(2)} ` + Dictionary[this.language]["keyPassMap"]["chart"]["xAPerPass"],textColor);
      this.writeText(svg, pitchWidth * 0.51 + margin.left, 0.59 * pitchWidth + margin.top, "start", (0.016 * pitchWidth) + "px", `${ awayShots.length - awayPenaltyGoals } ` + Dictionary[this.language]["keyPassMap"]["chart"]["keyPasses"] + ` · ${ awayXAPerPassValue.toFixed(2)} ` + Dictionary[this.language]["keyPassMap"]["chart"]["xAPerPass"],textColor);

    },
    renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const passLegend = language === 'en' ? 'passes' : 'pases';

      const goalArea = shots.filter(s => s.x >= 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
      const boxRight = shots.filter(s => s.x >= 83 && s.y >= 21.1 && s.y < 36.8).length;
      const boxCenter = shots.filter(s => s.x >= 83 && s.x < 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
      const boxLeft = shots.filter(s => s.x >= 83 && s.y > 63.2 && s.y <= 78.9).length;
      //Out Of Box
      const outBoxDeepRight = shots.filter(s => s.x >= 83 && s.y < 21.1).length;
      const outBoxDeepLeft = shots.filter(s => s.x >= 83 && s.y > 78.9).length;
      const outBoxRight = shots.filter(s => s.x >= 65 && s.x < 83 && s.y < 21.1).length;
      const outBoxCentreRight = shots.filter(s => s.x >= 65 && s.x < 83 && s.y >= 21.1 && s.y <= 35.5).length;
      const outBoxCentre = shots.filter(s => s.x >= 65 && s.x < 83 && s.y >= 35.6 && s.y <= 64.4).length;
      const outBoxCentreLeft = shots.filter(s => s.x >= 65 && s.x < 83 && s.y >= 64.5 && s.y <= 78.9).length;
      const outBoxLeft = shots.filter(s => s.x >= 65 && s.x < 83 && s.y > 78.9).length;
      //+35
      const right35 = shots.filter(s => s.x >= 50 && s.x < 65 && s.y < 21.1).length;
      const centreRight35 = shots.filter(s => s.x >= 50 && s.x < 65 && s.y >= 21.1 && s.y <= 35.5).length;
      const centre35 = shots.filter(s => s.x >= 50 && s.x < 65 && s.y >= 35.6 && s.y <= 64.4).length;
      const centreLeft35 = shots.filter(s => s.x >= 50 && s.x < 65 && s.y >= 64.5 && s.y <= 78.9).length;
      const left35 = shots.filter(s => s.x >= 50 && s.x < 65 && s.y > 78.9).length;

      var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.7]);

      if (outBoxDeepLeft > 0.5) {
        this.drawRectangle(svg, 0.094 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepLeft), "gameLegend1");
        this.writeText(svg, 0.177 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepLeft / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.177 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepLeft) } ${ passLegend })`,textColor);
      }
      if (outBoxLeft > 0.5) {
        this.drawRectangle(svg, 0.094 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxLeft), "gameLegend1");
        this.writeText(svg, 0.177 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxLeft / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.177 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxLeft) } ${ passLegend })`,textColor);
      }
      if (left35 > 0.5) {
        this.drawRectangle(svg, 0.094 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(left35), "gameLegend1");
        this.writeText(svg, 0.177 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(left35 / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.177 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(left35) } ${ passLegend })`,textColor);
      }
      if (outBoxDeepRight > 0.5) {
        this.drawRectangle(svg, 0.76 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepRight), "gameLegend1");
        this.writeText(svg, 0.842 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepRight / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.842 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepRight) } ${ passLegend })`,textColor);
      }
      if (outBoxRight > 0.5) {
        this.drawRectangle(svg, 0.76 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxRight), "gameLegend1");
        this.writeText(svg, 0.842 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxRight / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.842 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxRight) } ${ passLegend })`,textColor);
      }
      if (right35 > 0.5) {
        this.drawRectangle(svg, 0.76 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(right35), "gameLegend1");
        this.writeText(svg, 0.842 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(right35 / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.842 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(right35) } ${ passLegend })`,textColor);
      }

      if (outBoxCentreLeft > 0.5) {
        this.drawRectangle(svg, 0.254 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreLeft), "gameLegend1");
        this.writeText(svg, 0.33 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreLeft / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.33 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreLeft) } ${ passLegend })`,textColor);
      }
      if (outBoxCentre > 0.5) {
        this.drawRectangle(svg, 0.39 * svgWidth, 0.365 * pitchHeight, 0.23 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentre), "gameLegend1");
        this.writeText(svg, 0.505 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentre / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.505 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentre) } ${ passLegend })`,textColor);
      }
      if (outBoxCentreRight > 0.5) {
        this.drawRectangle(svg, 0.625 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreRight), "gameLegend1");
        this.writeText(svg, 0.69 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreRight / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.69 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreRight) } ${ passLegend })`,textColor);
      }

      if (centreLeft35 > 0.5) {
        this.drawRectangle(svg, 0.254 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreLeft35), "gameLegend1");
        this.writeText(svg, 0.33 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreLeft35 / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.33 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreLeft35) } ${ passLegend })`,textColor);
      }

      if (centre35 > 0.5) {
        this.drawRectangle(svg, 0.39 * svgWidth, 0.665 * pitchHeight, 0.23 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centre35), "gameLegend1");
        this.writeText(svg, 0.505 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centre35 / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.505 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centre35) } ${ passLegend })`,textColor);
      }
      if (centreRight35 > 0.5) {
        this.drawRectangle(svg, 0.625 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreRight35), "gameLegend1");
        this.writeText(svg, 0.69 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreRight35 / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.69 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreRight35) } ${ passLegend })`,textColor);
      }
      if (boxLeft > 0.5) {
        this.drawRectangle(svg, 0.265 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxLeft), "gameLegend1");
        this.writeText(svg, 0.33 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxLeft / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.33 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxLeft) } ${ passLegend })`,textColor);
      }
      if (boxRight > 0.5) {
        this.drawRectangle(svg, 0.625 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxRight), "gameLegend1");
        this.writeText(svg, 0.69 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxRight / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.69 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxRight) } ${ passLegend })`,textColor);
      }
      if (goalArea > 0.5) {
        this.drawRectangle(svg, 0.4 * svgWidth, 0.105 * pitchHeight, 0.21 * svgWidth, 0.068 * pitchHeight, "red", shadowScale(goalArea), "gameLegend1");
        this.writeText(svg, 0.505 * svgWidth, 0.11 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalArea / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.505 * svgWidth, 0.14 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalArea) } ${ passLegend })`,textColor);
      }
      if (boxCenter > 0.5) {
        this.drawRectangle(svg, 0.39 * svgWidth, 0.19 * pitchHeight, 0.23 * svgWidth, 0.155 * pitchHeight, "red", shadowScale(boxCenter), "gameLegend1");
        this.writeText(svg, 0.505 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxCenter / shots.length * 100) } %`,textColor);
        this.writeText(svg, 0.505 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxCenter) } ${ passLegend })`,textColor);
      }

    },
    onResize(){
      let elto =document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    delete this.observer;
  },
}

</script>



// WEBPACK FOOTER //
// src/views/base/XAChart.vue